import { lazy, ComponentType } from "react";
import { LucideIcon, Home, Image, Package, FileText, Shield, Scale, LogIn, Lock, Upload, Users, Mail, Instagram, Flame } from "lucide-react";

// Kritischer Admin-Auth-Pfad: eager importieren, damit der Login/MFA-Flow
// nicht an einem fehlgeschlagenen dynamic import scheitert
// (z. B. veralteter Service-Worker-Cache, Netzabbruch, alter Chunk-Hash).
import Auth from "@/pages/Auth";
import AdminMfa from "@/pages/AdminMfa";

// Lazy-loaded Page Components
const Index = lazy(() => import("@/pages/Index"));
const Galerie = lazy(() => import("@/pages/Galerie"));
const Materialien = lazy(() => import("@/pages/Materialien"));
const Lasergravur = lazy(() => import("@/pages/Lasergravur"));
const Impressum = lazy(() => import("@/pages/Impressum"));
const Datenschutz = lazy(() => import("@/pages/Datenschutz"));
const Agb = lazy(() => import("@/pages/Agb"));
const Intern = lazy(() => import("@/pages/Intern"));
const SitemapXml = lazy(() => import("@/pages/SitemapXml"));
const RobotsTxt = lazy(() => import("@/pages/RobotsTxt"));
const Unsubscribe = lazy(() => import("@/pages/Unsubscribe"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const OAuthConsent = lazy(() => import("@/pages/OAuthConsent"));
const NotFound = lazy(() => import("@/pages/NotFound"));

export interface RouteConfig {
  path: string;
  component: ComponentType;
  priority: number;
  changefreq: "always" | "hourly" | "daily" | "weekly" | "monthly" | "yearly" | "never";
  includeInSitemap: boolean;
  isProtected: boolean;
  requireAdmin?: boolean;
  title?: string;
}

export type NavItemType = "page" | "hash" | "external";

export interface NavItem {
  type: NavItemType;
  path: string;           // Route path, hash, or external URL
  label: string;          // Display label
  icon?: LucideIcon;      // Optional icon
  showInDesktop: boolean;
  showInMobile: boolean;
  requireAdmin?: boolean; // Only show for admins
}

// Zentrale Route-Konfiguration für SEO, Sitemap und Router
export const routeConfigs: RouteConfig[] = [
  {
    path: "/",
    component: Index,
    priority: 1.0,
    changefreq: "weekly",
    includeInSitemap: true,
    isProtected: false,
    title: "Startseite",
  },
  {
    path: "/galerie",
    component: Galerie,
    priority: 0.8,
    changefreq: "weekly",
    includeInSitemap: true,
    isProtected: false,
    title: "Galerie",
  },
  {
    path: "/materialien",
    component: Materialien,
    priority: 0.8,
    changefreq: "monthly",
    includeInSitemap: true,
    isProtected: false,
    title: "Materialien",
  },
  {
    path: "/lasergravur",
    component: Lasergravur,
    priority: 0.8,
    changefreq: "monthly",
    includeInSitemap: true,
    isProtected: false,
    title: "Lasergravur",
  },
  {
    path: "/impressum",
    component: Impressum,
    priority: 0.3,
    changefreq: "yearly",
    includeInSitemap: true,
    isProtected: false,
    title: "Impressum",
  },
  {
    path: "/datenschutz",
    component: Datenschutz,
    priority: 0.3,
    changefreq: "yearly",
    includeInSitemap: true,
    isProtected: false,
    title: "Datenschutz",
  },
  {
    path: "/agb",
    component: Agb,
    priority: 0.3,
    changefreq: "yearly",
    includeInSitemap: true,
    isProtected: false,
    title: "AGB",
  },
  {
    path: "/auth",
    component: Auth,
    priority: 0,
    changefreq: "never",
    includeInSitemap: false,
    isProtected: false,
    title: "Anmeldung",
  },
  {
    path: "/intern",
    component: Intern,
    priority: 0,
    changefreq: "never",
    includeInSitemap: false,
    isProtected: true,
    requireAdmin: true,
    title: "Admin-Bereich",
  },
  // Technische Routen (nicht in Sitemap)
  {
    path: "/sitemap.xml",
    component: SitemapXml,
    priority: 0,
    changefreq: "never",
    includeInSitemap: false,
    isProtected: false,
  },
  {
    path: "/robots.txt",
    component: RobotsTxt,
    priority: 0,
    changefreq: "never",
    includeInSitemap: false,
    isProtected: false,
  },
  {
    path: "/unsubscribe",
    component: Unsubscribe,
    priority: 0,
    changefreq: "never",
    includeInSitemap: false,
    isProtected: false,
    title: "E-Mail-Abmeldung",
  },
  {
    path: "/reset-password",
    component: ResetPassword,
    priority: 0,
    changefreq: "never",
    includeInSitemap: false,
    isProtected: false,
    title: "Passwort zurücksetzen",
  },
  {
    path: "/.lovable/oauth/consent",
    component: OAuthConsent,
    priority: 0,
    changefreq: "never",
    includeInSitemap: false,
    isProtected: false,
    title: "Zugriff erlauben",
  },
  {
    path: "/admin-mfa",
    component: AdminMfa,
    priority: 0,
    changefreq: "never",
    includeInSitemap: false,
    isProtected: false,
    title: "Zwei-Faktor-Authentifizierung",
  },
  // Catch-all Route (muss am Ende sein)
  {
    path: "*",
    component: NotFound,
    priority: 0,
    changefreq: "never",
    includeInSitemap: false,
    isProtected: false,
  },
];

// Navigation Items - Zentrale Konfiguration für Header-Navigation
export const navItems: NavItem[] = [
  {
    type: "page",
    path: "/",
    label: "Start",
    icon: Home,
    showInDesktop: true,
    showInMobile: true,
  },
  {
    type: "page",
    path: "/galerie",
    label: "Galerie",
    icon: Image,
    showInDesktop: true,
    showInMobile: true,
  },
  {
    type: "page",
    path: "/materialien",
    label: "Materialien",
    icon: Package,
    showInDesktop: true,
    showInMobile: true,
  },
  {
    type: "page",
    path: "/lasergravur",
    label: "Lasergravur",
    icon: Flame,
    showInDesktop: true,
    showInMobile: true,
  },
  {
    type: "page",
    path: "/materialien#material-finder",
    label: "Material-Finder",
    icon: Package,
    showInDesktop: true,
    showInMobile: true,
  },
  {
    type: "hash",
    path: "#upload",
    label: "Upload",
    icon: Upload,
    showInDesktop: true,
    showInMobile: true,
  },
  {
    type: "hash",
    path: "#about",
    label: "Über uns",
    icon: Users,
    showInDesktop: true,
    showInMobile: true,
  },
  {
    type: "hash",
    path: "#kontakt",
    label: "Kontakt",
    icon: Mail,
    showInDesktop: true,
    showInMobile: true,
  },
  {
    type: "external",
    path: "https://www.instagram.com/3d_druck_bolz/",
    label: "Instagram",
    icon: Instagram,
    showInDesktop: true,
    showInMobile: true,
  },
  {
    type: "page",
    path: "/intern",
    label: "Intern",
    icon: Lock,
    showInDesktop: true,
    showInMobile: true,
    requireAdmin: true,
  },
];

// Helper-Funktion um öffentliche Routen für Sitemap zu filtern
export const getPublicRoutes = (): RouteConfig[] => {
  return routeConfigs.filter(
    (route) => route.includeInSitemap && !route.isProtected
  );
};

// Helper-Funktion um Route-Config nach Pfad zu finden
export const getRouteConfig = (path: string): RouteConfig | undefined => {
  return routeConfigs.find((route) => route.path === path);
};

// Helper-Funktion um alle navigierbaren Routen zu bekommen (für Navigation)
export const getNavigableRoutes = (): RouteConfig[] => {
  return routeConfigs.filter(
    (route) => 
      route.includeInSitemap && 
      !route.isProtected && 
      route.path !== "*"
  );
};

// Helper-Funktion für Desktop-Navigation-Items
export const getDesktopNavItems = (): NavItem[] => {
  return navItems.filter((item) => item.showInDesktop);
};

// Helper-Funktion für Mobile-Navigation-Items
export const getMobileNavItems = (): NavItem[] => {
  return navItems.filter((item) => item.showInMobile);
};
